import LangEnum from '../../../../../../lang/LangEnum';
import PriceCompositionUtils from '../../../../../products/prices/utils/priceComposition.utils';
import { OrderItemPriceComposition, OrderItemPriceCompositionItem } from '../../../../order.types';
import { GetOrderItemsStatus, HeaderSection } from '../../../../products/orderProducts.types';

const isCostComposition = ({ label }: OrderItemPriceCompositionItem) =>
  PriceCompositionUtils.isCost(label);

export const sortPriceCompositionForSummary = (priceComposition: OrderItemPriceComposition) =>
  [...priceComposition].sort((a, b) => Number(isCostComposition(a)) - Number(isCostComposition(b)));

const EMPTY_INCOME_STATUS: GetOrderItemsStatus = {
  pendingQuantity: 0,
  settledQuantity: 0,
  canceledQuantity: 0,
  reservedQuantity: 0,
  pendingValue: 0,
  settledValue: 0,
  canceledValue: 0,
  reservedValue: 0,
};

export const sumSectionsIncomeStatus = (sections: HeaderSection[]): GetOrderItemsStatus =>
  sections.reduce<GetOrderItemsStatus>(
    (accumulated, { incomeStatus }) => ({
      pendingQuantity: accumulated.pendingQuantity + (incomeStatus?.pendingQuantity ?? 0),
      settledQuantity: accumulated.settledQuantity + (incomeStatus?.settledQuantity ?? 0),
      canceledQuantity: accumulated.canceledQuantity + (incomeStatus?.canceledQuantity ?? 0),
      reservedQuantity: accumulated.reservedQuantity + (incomeStatus?.reservedQuantity ?? 0),
      pendingValue: accumulated.pendingValue + (incomeStatus?.pendingValue ?? 0),
      settledValue: accumulated.settledValue + (incomeStatus?.settledValue ?? 0),
      canceledValue: accumulated.canceledValue + (incomeStatus?.canceledValue ?? 0),
      reservedValue: accumulated.reservedValue + (incomeStatus?.reservedValue ?? 0),
    }),
    { ...EMPTY_INCOME_STATUS },
  );

export const sumSectionsTotalValue = (sections: HeaderSection[]) =>
  sections.reduce((accumulated, { total }) => accumulated + (total ?? 0), 0);

export const hasIncomeMovement = ({
  settledQuantity,
  reservedQuantity,
  pendingQuantity,
  canceledQuantity,
}: GetOrderItemsStatus) =>
  settledQuantity + reservedQuantity + pendingQuantity + canceledQuantity > 0;

interface IncomeStatusRow {
  label: LangEnum;
  quantity: number;
  value: number;
  percent: number;
}

export const buildIncomeStatusRows = (
  incomeStatus: GetOrderItemsStatus,
  totalValue: number,
): IncomeStatusRow[] => {
  const totalQuantity =
    incomeStatus.settledQuantity +
    incomeStatus.reservedQuantity +
    incomeStatus.pendingQuantity +
    incomeStatus.canceledQuantity;

  const toPercent = (value: number) => (totalValue ? Math.round((value / totalValue) * 100) : 0);

  return [
    { label: LangEnum.GLOBAL_ORDER, quantity: totalQuantity, value: totalValue },
    {
      label: LangEnum.ORDER_ITEM_STATUS_SETTLED,
      quantity: incomeStatus.settledQuantity,
      value: incomeStatus.settledValue,
    },
    {
      label: LangEnum.ORDER_ITEM_STATUS_RESERVED,
      quantity: incomeStatus.reservedQuantity,
      value: incomeStatus.reservedValue,
    },
    {
      label: LangEnum.ORDER_ITEM_STATUS_PENDING,
      quantity: incomeStatus.pendingQuantity,
      value: incomeStatus.pendingValue,
    },
    {
      label: LangEnum.ORDER_ITEM_STATUS_CANCELED,
      quantity: incomeStatus.canceledQuantity,
      value: incomeStatus.canceledValue,
    },
  ].map((row) => ({ ...row, percent: toPercent(row.value) }));
};
